import {
  CardDisputeAccountStatus,
  CardDisputeCurrencyError,
  type CardDisputeFraudReasonDetails,
  CardDisputeMerchandiseOrServices,
  CardDisputeMerchantContactMethod,
  CardDisputeOriginalCreditNotAcceptedReason,
  CardDisputeOtherPaymentProof,
  CardDisputeReservationCertification,
  CardDisputeReturnMethod,
  CardDisputeReturnRefusalReason,
  CardDisputeServiceType,
  CardDisputeTransactionCodeError,
} from '@column/api';

type FraudType = NonNullable<CardDisputeFraudReasonDetails['fraud_type']>;

/**
 * Every fraud type, including those stored disputes may still carry but that can no longer be filed as fraud:
 * `acquirer_reported`, `merchant_misrepresentation` and `manipulation_of_account_holder`.
 */
export const FRAUD_TYPE_LABELS: Record<FraudType, string> = {
  lost: 'Card lost',
  stolen: 'Card stolen',
  card_not_received_as_issued: 'Card not received as issued',
  fraudulent_application: 'Fraudulent application',
  counterfeit: 'Counterfeit card',
  miscellaneous_account_takeover: 'Miscellaneous account takeover',
  card_absent: 'Card-absent transaction',
  acquirer_reported: 'Fraud reported by the acquirer',
  incorrect_processing: 'Incorrect processing',
  account_or_credentials_takeover: 'Account or credentials takeover',
  merchant_misrepresentation: 'Merchant misrepresentation',
  manipulation_of_account_holder: 'Manipulation of the account holder',
};

export const ACCOUNT_STATUS_LABELS: Record<CardDisputeAccountStatus, string> = {
  [CardDisputeAccountStatus.ACCOUNT_CLOSED]: 'Account was closed',
  [CardDisputeAccountStatus.CREDIT_PROBLEM]: 'Account had a credit problem',
  [CardDisputeAccountStatus.FRAUD]: 'Account was flagged for fraud',
};

export const MERCHANDISE_OR_SERVICES_LABELS: Record<CardDisputeMerchandiseOrServices, string> = {
  [CardDisputeMerchandiseOrServices.MERCHANDISE]: 'Merchandise',
  [CardDisputeMerchandiseOrServices.SERVICES]: 'Services',
};

export const MERCHANT_CONTACT_METHOD_LABELS: Record<CardDisputeMerchantContactMethod, string> = {
  [CardDisputeMerchantContactMethod.EMAIL]: 'Email',
  [CardDisputeMerchantContactMethod.CALL_CENTER]: 'Call center',
  [CardDisputeMerchantContactMethod.MAIL]: 'Mail',
  [CardDisputeMerchantContactMethod.SMS]: 'SMS',
  [CardDisputeMerchantContactMethod.IN_PERSON]: 'In person',
  [CardDisputeMerchantContactMethod.WEBFORM]: 'Webform or app',
};

export const MERCHANT_RETURN_REFUSAL_REASON_LABELS: Record<CardDisputeReturnRefusalReason, string> = {
  [CardDisputeReturnRefusalReason.REFUSED_RETURN_AUTHORIZATION]: 'Merchant refused a return authorization',
  [CardDisputeReturnRefusalReason.REFUSED_ACCEPT_RETURNED_MERCHANDISE]:
    'Merchant refused to accept the returned merchandise',
  [CardDisputeReturnRefusalReason.ADVISED_NOT_TO_RETURN]:
    'Merchant advised the cardholder not to return the merchandise',
  [CardDisputeReturnRefusalReason.NO_LONGER_EXISTS_OR_NOT_RESPONDING]: 'Merchant no longer exists or is not responding',
  [CardDisputeReturnRefusalReason.DID_NOT_PROVIDE_CLEAR_RETURN_INSTRUCTIONS]:
    'Merchant did not provide clear return instructions',
};

export const RETURN_METHOD_LABELS: Record<CardDisputeReturnMethod, string> = {
  [CardDisputeReturnMethod.FACE_TO_FACE]: 'In person',
  [CardDisputeReturnMethod.FEDEX]: 'FedEx',
  [CardDisputeReturnMethod.DHL]: 'DHL',
  [CardDisputeReturnMethod.UPS]: 'UPS',
  [CardDisputeReturnMethod.POSTAL_SERVICE]: 'Postal service',
  [CardDisputeReturnMethod.OTHER]: 'Other',
};

export const OTHER_PAYMENT_PROOF_LABELS: Record<CardDisputeOtherPaymentProof, string> = {
  [CardDisputeOtherPaymentProof.ACQUIRER_REFERENCE_NUMBER]:
    'Acquirer reference number of a payment on another Visa card',
  [CardDisputeOtherPaymentProof.NETWORK_TRANSACTION_ID]:
    'Visa transaction identifier of a payment on another Visa card',
  [CardDisputeOtherPaymentProof.CASH_RECEIPT]: 'Cash receipt',
  [CardDisputeOtherPaymentProof.CANCELLED_CHECK]: 'Cancelled check',
  [CardDisputeOtherPaymentProof.THIRD_PARTY_VOUCHER]: 'Third-party voucher',
  [CardDisputeOtherPaymentProof.OTHER_CARD_STATEMENT]: 'Statement from the other card',
  [CardDisputeOtherPaymentProof.OTHER]: 'Other document',
};

export const CURRENCY_ERROR_LABELS: Record<CardDisputeCurrencyError, string> = {
  [CardDisputeCurrencyError.CURRENCY_DIFFERENCE]: 'Processed in a different currency than agreed',
  [CardDisputeCurrencyError.DYNAMIC_CURRENCY_CONVERSION]:
    'Converted with dynamic currency conversion the cardholder did not agree to',
};

export const TRANSACTION_CODE_ERROR_LABELS: Record<CardDisputeTransactionCodeError, string> = {
  [CardDisputeTransactionCodeError.CREDIT_INSTEAD_OF_REVERSAL]:
    'The merchant processed a credit instead of a reversal or adjustment',
  [CardDisputeTransactionCodeError.CREDIT_POSTED_AS_DEBIT]: 'A credit was posted as a debit',
  [CardDisputeTransactionCodeError.DEBIT_POSTED_AS_CREDIT]: 'A debit was posted as a credit',
};

export const SERVICE_TYPE_LABELS: Record<CardDisputeServiceType, string> = {
  [CardDisputeServiceType.TIMESHARE]: 'Timeshare',
  [CardDisputeServiceType.GUARANTEED_RESERVATION]: 'Guaranteed reservation',
  [CardDisputeServiceType.OTHER]: 'Other services',
};

export const RESERVATION_CERTIFICATION_LABELS: Record<CardDisputeReservationCertification, string> = {
  [CardDisputeReservationCertification.PROPERLY_CANCELED]:
    'The cardholder properly canceled the guaranteed reservation',
  [CardDisputeReservationCertification.ATTEMPTED_CANCELLATION_WITHIN_24_HOURS]:
    'The cardholder attempted to cancel within 24 hours of receiving the reservation confirmation',
  [CardDisputeReservationCertification.NO_SHOW_BILLED_FOR_MORE_THAN_ONE_DAY]:
    'The merchant billed a no-show for more than one day of accommodation or rental',
};

export const ORIGINAL_CREDIT_NOT_ACCEPTED_REASON_LABELS: Record<CardDisputeOriginalCreditNotAcceptedReason, string> = {
  [CardDisputeOriginalCreditNotAcceptedReason.RECIPIENT_REFUSED]: 'The recipient refused the original credit',
  [CardDisputeOriginalCreditNotAcceptedReason.PROHIBITED_BY_LOCAL_LAW]:
    'Local laws or regulations prohibit the original credit',
};

/** How a canceled recurring agreement ended. Dashboard-only: it selects which of Visa's two exclusive branches is sent. */
export const CANCELED_RECURRING_ENDED_BY_LABELS = {
  cardholder_cancellation: 'The cardholder canceled the agreement with the merchant',
  account_closure: 'You informed the merchant that the account was closed',
} as const;
